import type { DragEvent } from 'react';

import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import { getRoomKey } from '../utils';

import type { MediaImage, MediaSourceImage, PropertyRoomTypeWithImages } from './types';

export { getRoomKey };

const ALLOWED_IMAGE_UPLOAD_TYPES = ALLOWED_IMAGE_UPLOAD_ACCEPT.split(',');

export const imageSelectCheckboxSx: SystemStyleObject<Theme> = {
  position: 'absolute',
  top: 4,
  right: 4,
  bgcolor: 'common.white',
  borderRadius: '4px',
  p: 0.25,
  '&:hover': {
    bgcolor: 'grey.100',
  },
};

export interface UploadRoomAssignment {
  roomId: string;
  roomName: string;
}

export type UploadedRoomAssignment = UploadRoomAssignment & {
  publicId: string;
};

export function findPersistedRoomByKey(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined,
  roomKey: string,
) {
  return propertyRoomTypes?.find((room, index) => getRoomKey(room, index) === roomKey && room.id);
}

export function removeImageOverride(overrides: Record<string, boolean>, publicId: string) {
  const { [publicId]: _removedOverride, ...nextOverrides } = overrides;

  return nextOverrides;
}

function getFileExtension(filename: string) {
  return filename.split('.').pop()?.toLowerCase();
}

export function isAllowedImageFile(file: File) {
  const extension = getFileExtension(file.name);

  return (
    ALLOWED_IMAGE_UPLOAD_TYPES.includes(file.type) ||
    ALLOWED_IMAGE_UPLOAD_TYPES.includes(extension ? `.${extension}` : '')
  );
}

function normaliseRoomName(roomName: string) {
  return roomName
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getFileBasename(filename: string) {
  return filename.replace(/\.[^/.]+$/, '').trim();
}

function getBestRoomMatch(
  roomNameCandidate: string,
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
): UploadRoomAssignment | undefined {
  const normalisedRoomNameCandidate = normaliseRoomName(roomNameCandidate);
  let bestMatch: (UploadRoomAssignment & { score: number }) | undefined;

  propertyRoomTypes.forEach((room, index) => {
    if (!room.name) {
      return;
    }

    const normalisedRoomName = normaliseRoomName(room.name);
    const isExactMatch = normalisedRoomNameCandidate === normalisedRoomName;
    const isPrefixMatch = normalisedRoomNameCandidate.startsWith(`${normalisedRoomName} `);

    if (!isExactMatch && !isPrefixMatch) {
      return;
    }

    const score = isExactMatch ? Number.MAX_SAFE_INTEGER : normalisedRoomName.length;

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = {
        roomId: getRoomKey(room, index),
        roomName: room.name,
        score,
      };
    }
  });

  return bestMatch ? { roomId: bestMatch.roomId, roomName: bestMatch.roomName } : undefined;
}

export function getUploadRoomMatch(
  filePath: string,
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> = [],
): { roomId: string; roomName: string } | undefined {
  const pathParts = filePath.split('/').filter(Boolean);

  if (pathParts.length === 0) {
    return undefined;
  }

  const roomNameCandidates = pathParts.map((pathPart, index) =>
    index === pathParts.length - 1 ? getFileBasename(pathPart) : pathPart,
  );

  for (const roomNameCandidate of roomNameCandidates) {
    const roomMatch = getBestRoomMatch(roomNameCandidate, propertyRoomTypes);

    if (roomMatch) {
      return roomMatch;
    }
  }

  return undefined;
}

export function buildUploadPlans({
  files,
  propertyRoomTypes,
  roomAssignment,
}: {
  files: Array<File>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  roomAssignment?: UploadRoomAssignment;
}) {
  return files.map((file) => {
    return {
      file,
      roomAssignment: roomAssignment ?? getUploadRoomMatch(file.webkitRelativePath || file.name, propertyRoomTypes),
    };
  });
}

export function getMediaImageSourceKey(
  image: Pick<
    MediaImage,
    'publicId' | 'originalPublicId' | 'sourceImageId' | 'sourcePropertyId' | 'sourceRoomTypeId' | 'sourceType'
  >,
) {
  return [
    image.sourceType ?? 'unknown',
    image.sourcePropertyId,
    image.sourceRoomTypeId,
    image.sourceImageId,
    image.publicId,
  ].join(':');
}

export function dedupeMediaImages(images: Array<MediaImage>) {
  const seenPublicIdsBySource = new Map<string, Set<string>>();

  return images.filter((image) => {
    const sourceKey = `${image.sourceType ?? 'unknown'}:${image.sourceRoomId ?? ''}`;
    const seenPublicIds = seenPublicIdsBySource.get(sourceKey) ?? new Set<string>();

    if (seenPublicIds.has(image.publicId)) {
      return false;
    }

    seenPublicIds.add(image.publicId);
    seenPublicIdsBySource.set(sourceKey, seenPublicIds);
    return true;
  });
}

export function getSyncedImagePublicIds(
  currentPublicIds: Array<string>,
  images: Array<MediaImage>,
  publicIdReplacements: Record<string, string> = {},
) {
  const nextPublicIds = images.map((image) => image.publicId);
  const nextPublicIdSet = new Set(nextPublicIds);
  const existingPublicIds = currentPublicIds
    .map((publicId) => publicIdReplacements[publicId] ?? publicId)
    .filter((publicId) => nextPublicIdSet.has(publicId));
  const existingPublicIdSet = new Set(existingPublicIds);
  const newPublicIds = nextPublicIds.filter((publicId) => !existingPublicIdSet.has(publicId));

  return existingPublicIds.concat(newPublicIds);
}

export function isSameImageOrder(first: Array<string | undefined>, second: Array<string>) {
  return first.length === second.length && first.every((publicId, index) => publicId === second[index]);
}

export type RemovedRoomImageIdsByRoomKey = Record<string, Array<string>>;

export function isRemovedRoomGalleryImage(
  image: MediaImage,
  removedRoomImageIdsByRoomKey: RemovedRoomImageIdsByRoomKey,
) {
  if (image.sourceType !== 'room' || !image.sourceRoomId) {
    return false;
  }

  return (removedRoomImageIdsByRoomKey[image.sourceRoomId] ?? []).includes(image.publicId);
}

export function removePublicIdFromImageOrder(
  order: Array<string>,
  publicId: string,
  imagePublicIdReplacements: Record<string, string> = {},
) {
  const publicIdsToRemove = new Set([publicId]);

  Object.entries(imagePublicIdReplacements).forEach(([originalPublicId, replacementPublicId]) => {
    if (originalPublicId === publicId || replacementPublicId === publicId) {
      publicIdsToRemove.add(originalPublicId);
      publicIdsToRemove.add(replacementPublicId);
    }
  });

  return order.filter(
    (orderedPublicId) =>
      !publicIdsToRemove.has(orderedPublicId) &&
      !publicIdsToRemove.has(imagePublicIdReplacements[orderedPublicId] ?? orderedPublicId),
  );
}

export function syncGalleryOrderAfterDelete({
  remainingImages,
  publicId,
  imagePublicIdReplacements,
  savedOrder,
  dirtyOrder,
  deletedImageWasPending,
  hadSavedOrder,
}: {
  remainingImages: Array<MediaImage>;
  publicId: string;
  imagePublicIdReplacements: Record<string, string>;
  savedOrder?: Array<string>;
  dirtyOrder?: Array<string>;
  deletedImageWasPending: boolean;
  hadSavedOrder: boolean;
}) {
  const syncOrder = (order?: Array<string>) =>
    getSyncedImagePublicIds(
      removePublicIdFromImageOrder(order ?? [], publicId, imagePublicIdReplacements),
      remainingImages,
      imagePublicIdReplacements,
    );
  const nextSavedOrder = syncOrder(savedOrder);
  const nextDirtyOrder = dirtyOrder?.length ? syncOrder(dirtyOrder) : [];
  const savedBaseline = getSavedImageOrder({
    savedOrder: nextSavedOrder.length > 0 ? nextSavedOrder : undefined,
    images: remainingImages,
    imagePublicIdReplacements,
  });
  const clearedDirtyOrder =
    deletedImageWasPending && isSameImageOrder(savedBaseline, nextDirtyOrder)
      ? []
      : hadSavedOrder && isSameImageOrder(nextSavedOrder, nextDirtyOrder)
        ? []
        : nextDirtyOrder;

  return {
    nextSavedOrder,
    nextDirtyOrder: clearedDirtyOrder,
  };
}

export function getSavedImageOrder({
  savedOrder,
  images,
  imagePublicIdReplacements,
}: {
  savedOrder?: Array<string>;
  images: Array<MediaImage>;
  imagePublicIdReplacements: Record<string, string>;
}) {
  if (savedOrder?.length) {
    return getSyncedImagePublicIds(savedOrder, images, imagePublicIdReplacements);
  }

  return images.map((image) => image.publicId);
}

export function canPersistRoomImageHiddenState(
  image: MediaImage,
): image is MediaImage & { sourceImageId: string; sourcePropertyId: string; sourceRoomTypeId: string } {
  return !!image.sourceImageId && !!image.sourcePropertyId && !!image.sourceRoomTypeId;
}

export function buildOfferImageOrderUpdatePayload(images: Array<App.OfferImage>, orderedPublicIds: Array<string>) {
  const imagesByPublicId = new Map(images.map((image) => [image.id_cloudinary_external, image]));

  return orderedPublicIds.flatMap((publicId, index) => {
    const image = imagesByPublicId.get(publicId);

    if (!image?.id_image) {
      return [];
    }

    return [
      {
        id_image: image.id_image,
        id_cloudinary_external: publicId,
        order: index + 1,
      },
    ];
  });
}

export function buildRoomImageOrderUpdatePayload(images: Array<MediaSourceImage>, orderedPublicIds: Array<string>) {
  const imagesByPublicId = new Map<string, MediaSourceImage>();

  images.forEach((image) => {
    const publicId = image.id_cloudinary_external;

    if (publicId && !imagesByPublicId.has(publicId)) {
      imagesByPublicId.set(publicId, image);
    }
  });

  return orderedPublicIds.flatMap((publicId, index) => {
    const image = imagesByPublicId.get(publicId);

    if (!image?.id) {
      return [];
    }

    return [
      {
        id: image.id,
        id_cloudinary_external: publicId,
        order: index + 1,
      },
    ];
  });
}

function getSourceImages({
  images,
  seenImageKeys,
  sourceLabel,
  sourcePropertyId,
  sourceRoomId,
  sourceRoomTypeId,
  sourceType,
  useImageIdFallback = false,
}: {
  images?: Array<MediaSourceImage>;
  seenImageKeys: Set<string>;
  sourceLabel: string;
  sourcePropertyId?: string;
  sourceRoomId?: string;
  sourceRoomTypeId?: string;
  sourceType: MediaImage['sourceType'];
  useImageIdFallback?: boolean;
}): Array<MediaImage> {
  return (images ?? []).reduce<Array<MediaImage>>((mediaImages, image) => {
    const publicId = image.id_cloudinary_external;
    const rawSourceImageId = image.id ?? (useImageIdFallback ? image.id_image : undefined);
    const sourceImageId = rawSourceImageId === undefined ? undefined : String(rawSourceImageId);

    if (!publicId) {
      return mediaImages;
    }

    const imageKey = getMediaImageSourceKey({
      publicId,
      originalPublicId: image.original_id_cloudinary_external,
      sourceImageId,
      sourcePropertyId,
      sourceRoomTypeId,
      sourceType,
    });

    if (seenImageKeys.has(imageKey)) {
      return mediaImages;
    }

    seenImageKeys.add(imageKey);
    mediaImages.push({
      hidden: image.hidden,
      order: image.order,
      publicId,
      originalPublicId: image.original_id_cloudinary_external,
      sourceLabel,
      sourceImageId,
      sourcePropertyId,
      sourceRoomId,
      sourceRoomTypeId,
      sourceType,
      title: image.title || undefined,
    });

    return mediaImages;
  }, []);
}

export function buildMediaImages({
  offerImages,
  propertyRoomTypes,
}: {
  offerImages: Array<App.OfferImage>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}) {
  const seenImageKeys = new Set<string>();
  const offerGalleryImages = getSourceImages({
    images: offerImages,
    seenImageKeys,
    sourceLabel: 'Offer gallery',
    sourceType: 'offer',
    useImageIdFallback: true,
  });
  const roomImages = (propertyRoomTypes ?? []).flatMap((room, index) =>
    getSourceImages({
      images: room.images,
      seenImageKeys,
      sourceLabel: room.name || 'Room',
      sourcePropertyId: room.property_id,
      sourceRoomId: getRoomKey(room, index),
      sourceRoomTypeId: room.id,
      sourceType: 'room',
    }),
  );

  return offerGalleryImages.concat(roomImages);
}

export function getPrimaryVideoId(videos?: { luxuryescapes?: { vimeo_id?: string | null } }) {
  const luxuryEscapesVideoId = videos?.luxuryescapes?.vimeo_id;

  return luxuryEscapesVideoId && luxuryEscapesVideoId !== 'undefined' ? luxuryEscapesVideoId : undefined;
}

export function setFolderInputRef(node: HTMLInputElement | null) {
  node?.setAttribute('directory', '');
  node?.setAttribute('webkitdirectory', '');
}

export const UPLOAD_TILE_DRAG_LABEL = 'Drop images here';
export const UPLOAD_TILE_IDLE_LABEL = 'Drop images here or click to browse';

export const uploadTileTransitionSx: SystemStyleObject<Theme> = {
  transition: (theme) =>
    theme.transitions.create(['border-color', 'background-color', 'box-shadow', 'transform'], {
      duration: theme.transitions.duration.shortest,
    }),
};

export const uploadTileContentTransitionSx: SystemStyleObject<Theme> = {
  transition: (theme) =>
    theme.transitions.create(['color', 'transform'], { duration: theme.transitions.duration.shortest }),
};

export const uploadTileDragActiveSx: SystemStyleObject<Theme> = {
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: 'primary.main',
  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.16)' : 'primary.50'),
  boxShadow: (theme) => `0 0 0 4px ${theme.palette.primary.main}33`,
  transform: 'scale(1.01)',
};

export const uploadTileDragActiveContentSx: SystemStyleObject<Theme> = {
  color: 'primary.main',
  transform: 'scale(1.04)',
};

export const uploadButtonDragActiveSx: SystemStyleObject<Theme> = {
  bgcolor: 'action.hover',
  outline: 1,
  outlineColor: 'primary.main',
};

export function isLeavingDropZone(event: DragEvent) {
  const relatedTarget = event.relatedTarget;

  if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
    return false;
  }

  return true;
}

export function clearRemovedRoomImagePublicIds(
  removedRoomImageIdsByRoomKey: RemovedRoomImageIdsByRoomKey,
  roomKey: string,
  publicIds: Array<string>,
): RemovedRoomImageIdsByRoomKey {
  const publicIdSet = new Set(publicIds);
  const roomRemoved = removedRoomImageIdsByRoomKey[roomKey] ?? [];
  const nextRoomRemoved = roomRemoved.filter((id) => !publicIdSet.has(id));

  if (nextRoomRemoved.length === roomRemoved.length) {
    return removedRoomImageIdsByRoomKey;
  }

  const next = { ...removedRoomImageIdsByRoomKey };

  if (nextRoomRemoved.length > 0) {
    next[roomKey] = nextRoomRemoved;
  } else {
    delete next[roomKey];
  }

  return next;
}

export function clearDeletedOfferImageSourceKeys(deletedImageSourceKeys: Array<string>, publicIds: Array<string>) {
  const publicIdSet = new Set(publicIds);

  return deletedImageSourceKeys.filter((sourceKey) => {
    const publicId = sourceKey.split(':').pop();

    return !publicId || !publicIdSet.has(publicId);
  });
}

export function getStaleImportedOfferImageSourceKeys(
  offerImages: Array<App.OfferImage>,
  importedMediaImages: Array<MediaImage>,
) {
  const importedByPublicId = new Map(
    importedMediaImages
      .filter((image) => image.sourceType === 'offer')
      .map((image) => [image.publicId, image.sourceImageId] as const),
  );

  return offerImages.flatMap((offerImage) => {
    const publicId = offerImage.id_cloudinary_external;
    const importedSourceImageId = publicId ? importedByPublicId.get(publicId) : undefined;

    if (!publicId || !importedSourceImageId) {
      return [];
    }

    const sourceImageId = offerImage.id ?? offerImage.id_image;

    if (sourceImageId === undefined || String(sourceImageId) === importedSourceImageId) {
      return [];
    }

    return [
      getMediaImageSourceKey({
        publicId,
        sourceImageId: String(sourceImageId),
        sourceType: 'offer',
      }),
    ];
  });
}

export function getStaleImportedRoomImageSourceKeys(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined,
  roomKey: string,
  importedMediaImages: Array<MediaImage>,
) {
  const room = findPersistedRoomByKey(propertyRoomTypes, roomKey);
  const importedByPublicId = new Map(
    importedMediaImages
      .filter((image) => image.sourceType === 'room' && image.sourceRoomId === roomKey)
      .map((image) => [image.publicId, image.sourceImageId] as const),
  );

  return (room?.images ?? []).flatMap((roomImage) => {
    const publicId = roomImage.id_cloudinary_external;
    const importedSourceImageId = publicId ? importedByPublicId.get(publicId) : undefined;

    if (!publicId || !importedSourceImageId || !roomImage.id) {
      return [];
    }

    if (String(roomImage.id) === importedSourceImageId) {
      return [];
    }

    return [
      getMediaImageSourceKey({
        publicId,
        sourceImageId: String(roomImage.id),
        sourcePropertyId: room?.property_id,
        sourceRoomTypeId: room?.id,
        sourceType: 'room',
      }),
    ];
  });
}

export function buildRoomImageReplacePayload({
  mediaImages,
  room,
  roomKey,
  photoUnits,
}: {
  mediaImages: Array<MediaImage>;
  room: PropertyRoomTypeWithImages;
  roomKey: string;
  photoUnits: Array<{ id_cloudinary_external: string; title?: string }>;
}) {
  const currentRoomMediaImages = mediaImages.filter(
    (image) => image.sourceType === 'room' && image.sourceRoomId === roomKey && image.sourceImageId,
  );
  const mediaImagePublicIds = new Set(currentRoomMediaImages.map((image) => image.publicId));
  const persistedImagePayload: Array<Partial<MediaSourceImage>> = [];
  const seenPublicIds = new Set<string>();

  const addPersistedImage = (payload: Partial<MediaSourceImage>) => {
    const publicId = payload.id_cloudinary_external;

    if (!publicId || !payload.id || seenPublicIds.has(publicId)) {
      return;
    }

    seenPublicIds.add(publicId);
    persistedImagePayload.push({
      ...payload,
      order: payload.order ?? persistedImagePayload.length + 1,
    });
  };

  currentRoomMediaImages.forEach((image, index) => {
    addPersistedImage({
      id: image.sourceImageId,
      id_cloudinary_external: image.publicId,
      title: image.title,
      order: image.order ?? index + 1,
      hidden: image.hidden,
    });
  });

  (room.images ?? []).forEach((image) => {
    const publicId = image.id_cloudinary_external;

    if (!publicId || !image.id || mediaImagePublicIds.has(publicId)) {
      return;
    }

    addPersistedImage({
      id: image.id,
      id_cloudinary_external: publicId,
      title: image.title ?? undefined,
      order: image.order,
      hidden: image.hidden,
    });
  });

  const newImagePayload = photoUnits
    .filter((unit) => !seenPublicIds.has(unit.id_cloudinary_external))
    .map((unit, index) => ({
      id_cloudinary_external: unit.id_cloudinary_external,
      title: unit.title,
      order: persistedImagePayload.length + index + 1,
      hidden: false,
    }));

  return persistedImagePayload.concat(newImagePayload);
}

export function getSyncedRoomPublicIds(
  mediaImages: Array<MediaImage>,
  room: PropertyRoomTypeWithImages | undefined,
  roomKey: string,
) {
  return new Set(
    mediaImages
      .filter((image) => image.sourceType === 'room' && image.sourceRoomId === roomKey)
      .map((image) => image.publicId)
      .concat(
        (room?.images ?? [])
          .map((image) => image.id_cloudinary_external)
          .filter((publicId): publicId is string => !!publicId),
      ),
  );
}

export function getVisibleRoomImageCount(mediaImages: Array<MediaImage>, roomKey: string) {
  return mediaImages.filter((image) => image.sourceType === 'room' && image.sourceRoomId === roomKey && !image.hidden)
    .length;
}
